<template>
  <div class="p-table-container">
    <slot
      :auto-select-product-data="autoSelectProductData"
    />
    <el-table
      v-loading="itemsLoading"
      :data="items || []"
      style="width: 100%"
      size="mini"
      border
      @row-click="row => options.updateItem ? openUpdateItemDialog(row) : null"
    >
      <el-table-column
        v-if="expandLines && expandLines.length"
        type="expand"
      >
        <template slot-scope="scope">
          <p
            v-for="line in expandLines"
            :key="line.prop"
          >
            <strong>{{ line.label }}</strong>: {{ scope.row[line.prop] }}
          </p>
        </template>
      </el-table-column>
      <el-table-column
        v-if="options.displayStock"
      >
        <template slot-scope="scope">
          <div
            v-if="scope.row.stock !== undefined"
          >
            <el-tag
              size="mini"
              :type="scope.row.stock > 0 ? 'success' : 'danger'"
            >
              {{ scope.row.stock }} db készleten
            </el-tag>
          </div>
        </template>
      </el-table-column>
      <el-table-column
        v-for="column in columns"
        :key="column.prop"
        :align="column.align"
        :prop="column.prop"
        :label="column.label"
        :min-width="column.minWidth"
        :show-overflow-tooltip="column.showTooltip"
      >
        <template slot-scope="scope">
          <span v-if="column.format === 'currency'">{{ scope.row[column.prop] | currency(options.currency) }}</span>
          <span v-if="column.format === 'percent'">{{ scope.row[column.prop] }}%</span>
          <span v-if="column.format === 'unit'">{{ measureUnits.find(unit => unit.value === scope.row[column.prop]).label }}</span>
          <span v-if="!column.format && !column.formatter">{{ scope.row[column.prop] }}</span>
          <span v-if="column.formatter">{{ column.formatter(priceTypes, scope.row[column.prop]) }}</span>
        </template>
      </el-table-column>
      <el-table-column
        v-if="options.addItem || options.updateItem || options.deleteItem"
        :min-width="options.updateItem && options.deleteItem ? 150 : 65"
        align="right"
      >
        <template slot="header" slot-scope="scope">
          <el-button
            v-if="options.addItem"
            class="btn-item-action"
            size="mini"
            icon="el-icon-plus"
            type="success"
            @click="addItem"
          >
            Új sor
          </el-button>
        </template>
        <template slot-scope="scope">
          <el-button
            v-if="options.updateItem"
            class="btn-item-action"
            icon="el-icon-edit"
            size="mini"
            @click.stop="() => openUpdateItemDialog(scope.row)"
          >
            Módosítás
          </el-button>
          <el-button
            v-if="options.deleteItem"
            class="btn-item-action"
            icon="el-icon-delete"
            size="mini"
            type="danger"
            @click.stop="() => removeItem(scope.row)"
          >
            Törlés
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <invoice-item-dialog
      v-if="dialog === 'InvoiceItemDialog'"
      :default-item="selectedItem"
      :customer-id="customerId"
      :visible="updateDialogVisible"
      :currency="options.currency"
      @change="onDialogChange"
      @close="() => updateDialogVisible = false"
    />
    <stowage-item-dialog
      v-if="dialog === 'StowageItemDialog'"
      :default-item="selectedItem"
      :storage-id="storageId"
      :visible="updateDialogVisible"
      :currency="options.currency"
      :available-products="items || []"
      @change="onDialogChange"
      @close="() => updateDialogVisible = false"
    />
    <representative-special-price-dialog
      v-if="dialog === 'RepresentativeSpecialPriceDialog'"
      :default-item="selectedItem"
      :customer-id="customerId"
      :visible="updateDialogVisible"
      :currency="options.currency"
      @change="onDialogChange"
      @close="() => updateDialogVisible = false"
    />
    <ean-dialog
      v-if="dialog === 'EanDialog'"
      :default-item="selectedItem"
      :customer-id="customerId"
      :visible="updateDialogVisible"
      :currency="options.currency"
      @change="onDialogChange"
      @close="() => updateDialogVisible = false"
    />
    <person-dialog
      v-if="dialog === 'PersonDialog'"
      :default-item="selectedItem"
      :visible="updateDialogVisible"
      @change="onDialogChange"
      @close="() => updateDialogVisible = false"
    />
    <person-task-dialog
      v-if="dialog === 'PersonTaskDialog'"
      :default-item="selectedItem"
      :visible="updateDialogVisible"
      @change="onDialogChange"
      @close="() => updateDialogVisible = false"
    />
    <price-dialog
      v-if="dialog === 'PriceDialog'"
      :default-item="selectedItem"
      :visible="updateDialogVisible"
      @change="onDialogChange"
      @close="() => updateDialogVisible = false"
    />
  </div>
</template>

<script>
import './style.scss';
import InvoiceItemDialog from './dialogs/InvoiceItemDialog';
import StowageItemDialog from './dialogs/StowageItemDialog';
import RepresentativeSpecialPriceDialog from './dialogs/RepresentativeSpecialPriceDialog';
import EanDialog from './dialogs/EanDialog';
import PersonDialog from './dialogs/PersonDialog';
import PriceDialog from './dialogs/PriceDialog';
import PersonTaskDialog from './dialogs/PersonTaskDialog';
import measureUnits from '../measureUnits';
import moment from 'moment';

export default {
  components: {
    RepresentativeSpecialPriceDialog,
    InvoiceItemDialog,
    PersonDialog,
    PersonTaskDialog,
    PriceDialog,
    EanDialog,
    StowageItemDialog,
  },
  computed: {
    priceTypes() {
      return this.$store.getters['priceType/priceTypes'];
    },
  },
  props: {
    emptyLine: {
      type: Object,
      default() {
        return {};
      },
      required: false,
    },
    columns: {
      type: Array,
      default() {
        return [];
      },
      required: false,
    },
    expandLines: {
      type: Array,
      default() {
        return [];
      },
      required: false,
    },
    data: {
      type: Array,
      default() {
        return [];
      },
      required: false,
    },
    dialog: {
      type: String,
      default: '',
      required: false,
    },
    customerId: {
      type: Number,
      default: 0,
      required: false,
    },
    storageId: {
      type: Number,
      default: 0,
      required: false,
    },
    endpoint: {
      type: String,
      required: false,
    },
    opts: {
      type: Object,
      default() {
        return {};
      },
      required: false,
    },
  },
  data() {
    return {
      stocks: null,
      measureUnits,
      updateDialogVisible: false,
      items: [],
      selectedItem: {},
      itemsLoading: false,
      options: {
        currency: 'HUF',
        editableRows: true,
        deletableRows: true,
        displayStock: false,
      },
    };
  },
  watch: {
    opts() {
      this.options = { ...this.options, ...this.opts };
    },
    endpoint() {
      this.loadItems();
    },
  },
  async mounted() {
    this.options = { ...this.options, ...this.opts };
    if (this.data && this.data.length) {
      this.items = [...this.data];
    } else {
      await this.loadItems();
    }
    await this.loadStocks()
  },
  methods: {
    openUpdateItemDialog(item) {
      this.updateDialogVisible = true;
      this.selectedItem = item;
    },
    async loadStocks() {
      if (!this.storageId) {
        return;
      }
      this.stockLoading = true;
      const stocks = await axios.post('api/products/stocks', {
        productIds: this.items.map(item => item.product_id),
        storageId: this.storageId
      })
      this.stocks = stocks;
      const itemsWithStock = this.items.map(item => {
        let fullStock = 0;
        const productStocks = stocks.filter(stock => stock.product_id === item.product_id);
        productStocks.forEach(stock => fullStock += stock.count * stock.count_per_pack);
        return {
          ...item,
          stock: fullStock
        };
      })
      this.items = [
        ...itemsWithStock,
      ];
      this.stockLoading = false;
    },
    async autoSelectProductData() {
      if (!this.stocks || this.stocks.length === 0) {
        await this.loadStocks();
      }
      const itemsWithSelectedStock = this.items.map(item => {
        const productStocks = this.stocks.filter(stock => {
          return stock.product_id === item.product_id && stock.product_pack_type_id === item.product_pack_type_id && stock.count >= item.count;
        });
        let expirationDate = null;
        let supplierId = null;
        let supplierName = null;
        productStocks.forEach(productStock => {
          if (!expirationDate) {
            expirationDate = productStock.expiration_date;
            supplierId = productStock.supplier_id;
            supplierName = productStock.supplier_name;
          }
          if (moment(expirationDate).isAfter(productStock.expiration_date)) {
            expirationDate = productStock.expiration_date;
            supplierId = productStock.supplier_id;
            supplierName = productStock.supplier_name;
          }
        });
        return {
          ...item,
          supplier_id: supplierId,
          supplier_name: supplierName,
          expiration_date: expirationDate,
        };
      });
      this.items = [
        ...itemsWithSelectedStock
      ];
      this.$messageWrapper.success('Beszállítók és szavatossági idők kiválasztása megtörtént.');
    },
    async loadItems() {
      this.itemsLoading = true;
      if (this.endpoint) {
        this.items = (await axios.get(this.endpoint)).map(item => ({
          ...item,
          key: Math.random()
            .toString(36)
            .substring(7)
        }));
        this.$emit('change');
      } else if (this.data) {
        this.items = this.data.map(item => ({
          ...item,
          key: Math.random()
          .toString(36)
          .substring(7)
        }));
        this.$emit('change');
      }
      this.itemsLoading = false;
    },
    onDialogChange(item) {
      if (item.id || item.key) {
        this.items = this.items.map(i => {
          return ((i.id && item.id && i.id === item.id) || (i.key && item.key && i.key === item.key)) ? item : i;
        });
      } else {
        item.key = Math.random()
          .toString(36)
          .substring(7);
        this.items = [...this.items, item];
      }
      this.$emit('change');
      this.loadStocks();
    },
    removeItem(item) {
      this.items = this.items.filter(i => i.id !== item.id || (i.key && item.key && i.key !== item.key));
      this.$emit('change');
    },
    createItem(item) {
      item.key = Math.random()
        .toString(36)
        .substring(7);
      this.items = [...this.items, item];
      this.loadStocks();
    },
    clear() {
      this.items = [];
    },
    addItem() {
      this.openUpdateItemDialog(this.emptyLine);
      this.$emit('change');
    },
    getItems() {
      return this.items;
    },
    getPrices() {
      let netPrice = 0;
      let grossPrice = 0;
      this.items.forEach(item => {
        netPrice += item.net_price;
        grossPrice += item.gross_price;
      });
      return {
        netPrice,
        grossPrice,
      };
    },
  },
};
</script>
